import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from '@storable/canaveral-components';
import { Col, Row, Tabs, Tab } from 'react-bootstrap';
import CurrentSiteEdit from '../../containers/CurrentSiteEdit';
import FacilityBrandingForm from '../FacilityBrandingForm';
import MediaGallerySelector from '../../containers/MediaGallerySelector';
import { LimitContextProvider } from '../../contexts/LimitContext';
import { MANAGE } from '../../../modules/authorization/actions';
import { NON_FACILITY_PAGES, FACILITY_PAGES } from '../../../src/shared/abilities';
import useCurrentUser from '../../hooks/useCurrentUser';

export const SITE_BRANDING_TAB = 'site';
export const FACILITY_BRANDING_TAB = 'facility';
export const SITE_LOGO_TAB = 'logo';
export const SITE_SOCIAL_MEDIA_TAB = 'socialMedia';

const BrandingModal = ({ onDismiss, initialTab, initFacility, ...rest }) => {
  const currentUser = useCurrentUser();
  const nonFacilityAuthorized = currentUser.can(MANAGE, NON_FACILITY_PAGES);
  const facilityAuthorized = currentUser.can(MANAGE, FACILITY_PAGES);

  const [tab, setTab] = useState(initialTab);
  const [showMediaGallery, setShowMediaGallery] = useState(false);
  const [selectedMedium, setSelectedMedium] = useState();
  const [usedMedia, setUsedMedia] = useState([]);
  const [activeSection, setActiveSection] = useState();
  const [appliedMedia, setAppliedMedia] = useState({});
  const [facility, setFacility] = useState(initFacility);
  const [logoEnabled, setLogoEnabled] = useState();
  const [socialMediaEnabled, setSocialMediaEnabled] = useState();

  useEffect(() => {
    const defaultTab = (!nonFacilityAuthorized || initialTab === FACILITY_BRANDING_TAB)
      ? FACILITY_BRANDING_TAB
      : SITE_BRANDING_TAB;
    setTab(defaultTab);
  }, [initialTab, nonFacilityAuthorized]);

  const applyMedium = useCallback((section, medium) => {
    setAppliedMedia(prev => ({ ...prev, [section]: medium }));
    setShowMediaGallery(false);
  }, []);

  const handleSetMedium = useCallback(() => {
    applyMedium(activeSection, selectedMedium);
  }, [activeSection, selectedMedium, applyMedium]);

  const handleDismiss = useCallback(() => {
    setAppliedMedia({});
    setActiveSection(null);
    setLogoEnabled(null);
    setSocialMediaEnabled(null);
    onDismiss();
  }, [onDismiss]);

  const commonEditProps = useMemo(() => ({
    ...rest,
    appliedMedia,
    onDismiss: handleDismiss,
    selectedMedium,
    setActiveSection,
    setAppliedMedium: applyMedium,
    setSelectedMedium,
    setShowMediaGallery,
    setUsedMedia,
    showMediaGallery,
  }), [rest, appliedMedia, handleDismiss, selectedMedium, applyMedium]);

  const renderTabs = () => {
    const tabs = [];

    if (nonFacilityAuthorized) {
      tabs.push(
        <Tab eventKey={SITE_BRANDING_TAB} title="Style" key={SITE_BRANDING_TAB}>
          <CurrentSiteEdit {...commonEditProps} tabName={SITE_BRANDING_TAB} />
        </Tab>,
        <Tab eventKey={SITE_LOGO_TAB} title="Logo" key={SITE_LOGO_TAB}>
          <CurrentSiteEdit {...commonEditProps} tabName={SITE_LOGO_TAB} />
        </Tab>,
        <Tab eventKey={SITE_SOCIAL_MEDIA_TAB} title="Social Media" key={SITE_SOCIAL_MEDIA_TAB}>
          <CurrentSiteEdit {...commonEditProps} tabName={SITE_SOCIAL_MEDIA_TAB} />
        </Tab>,
      );
    }

    if (facilityAuthorized) {
      tabs.push(
        <Tab eventKey={FACILITY_BRANDING_TAB} title="Facility Branding" key={FACILITY_BRANDING_TAB}>
          <FacilityBrandingForm
            {...commonEditProps}
            setAppliedMedia={applyMedium}
            facility={facility}
            activeSection={activeSection}
            logoEnabled={logoEnabled}
            socialMediaEnabled={socialMediaEnabled}
            setFacility={setFacility}
            setLogoEnabled={setLogoEnabled}
            setSocialMediaEnabled={setSocialMediaEnabled}
          />
        </Tab>,
      );
    }

    return tabs;
  };

  return (
    <Modal onDismiss={handleDismiss} show={!!initialTab} size="lg">
      <Modal.Header>
        <Modal.Title id="media-gallery-branding">
          {showMediaGallery ? 'Media Gallery' : 'Branding'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showMediaGallery ? (
          <LimitContextProvider>
            <MediaGallerySelector
              usedMedia={usedMedia}
              selectedMedium={selectedMedium}
              setSelectedMedium={setSelectedMedium}
              showTrash={false}
              showSearch
            />
          </LimitContextProvider>
        ) : (
          <Row>
            <Col xs={12}>
              <Tabs
                id="branding-modal-tabs"
                activeKey={tab}
                onSelect={setTab}
                defaultActiveKey={SITE_BRANDING_TAB}
              >
                {renderTabs()}
              </Tabs>
            </Col>
          </Row>
        )}
      </Modal.Body>
      {showMediaGallery && (
        <Modal.Footer>
          <Button
            context="neutral"
            onClick={() => setShowMediaGallery(false)}
            size="sm"
          >
            {showMediaGallery ? 'Cancel' : 'Close'}
          </Button>
          <Button
            context="positive"
            disabled={!selectedMedium}
            onClick={handleSetMedium}
            size="sm"
          >
            Choose Image
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

BrandingModal.propTypes = {
  onDismiss: PropTypes.func.isRequired,
  initialTab: PropTypes.string.isRequired,
  initFacility: PropTypes.string,
};

BrandingModal.defaultProps = {
  initFacility: undefined,
};

export default BrandingModal;
